import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch, RootState } from "../../app/store";
import TenureInCurrentRole from "./TenureInCurrentRole";
import TenureWithTTEC from "./TenureWithTTEC";
import AreasOfDevelopment from "./AreasOfDevelopment";
import AreasOfStrengths from "./AreasOfStrengths";
import WillingnessToRelocate from "./WillingnessToRelocate";
import ERGParticipation from "./ERGParticipation";
import {
  DashboardPdfExportButton,
  exportDashboardToPdf,
} from "./DashboardPdfExport";

import { fetchDashboardGraphs } from "./getDashboardGraphsSlice";

const DashboardNewPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(
    (state: RootState) => state.dashboardGraphs
  );

  const [graphData, setGraphData] = React.useState<any>(null);
  const [showLoader, setShowLoader] = React.useState(true);
  const [pdfProgress, setPdfProgress] = React.useState<{
    show: boolean;
    current: number;
    total: number;
    message: string;
  }>({
    show: false,
    current: 0,
    total: 0,
    message: "",
  });

  useEffect(() => {
    dispatch(fetchDashboardGraphs());
  }, [dispatch]);

  const BlockingSpinner = () => (
    <div className="blocking-spinner-overlay">
      <div
        className="spinner-border text-primary blocking-spinner"
        role="status"
      >
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  useEffect(() => {
    if (dashboardData && dashboardData.data) {
      setGraphData(dashboardData);
      setShowLoader(false);
    }
  }, [dashboardData]);

  const handleExportToPdf = () => {
    exportDashboardToPdf(setPdfProgress);
  };

  const PdfProgressOverlay: React.FC = () => {
    return (
      <div
        className="blocking-spinner-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className="spinner-border text-primary mb-3"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="text-white mb-3" style={{ fontSize: "1.2rem" }}>
          {pdfProgress.message || "Generating PDF..."}
        </div>
        {pdfProgress.total > 0 && (
          <>
            <div className="text-white mb-2" style={{ fontSize: "1rem" }}>
              Processing {pdfProgress.current} of {pdfProgress.total}
            </div>
            <div className="progress" style={{ width: "50%", height: "10px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{
                  width: `${(pdfProgress.current / pdfProgress.total) * 100}%`,
                }}
                aria-valuenow={(pdfProgress.current / pdfProgress.total) * 100}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="my-4">Dashboard</h2>
          <DashboardPdfExportButton onExport={handleExportToPdf} />
        </div>
        <hr />
        <div className="row row-cols-1 row-cols-md-2 g-4">
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <TenureInCurrentRole
                  data={graphData?.data?.graphs?.tenureInCurrentRole}
                />
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <TenureWithTTEC
                  data={graphData?.data?.graphs?.tenureWithTTEC}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row row-cols-1 row-cols-md-2 g-4 mt-3">
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <WillingnessToRelocate
                  data={graphData?.data?.graphs?.willingnessToRelocate}
                />
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <ERGParticipation
                  data={graphData?.data?.graphs?.ergParticipation}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row row-cols-1 row-cols-md-2 g-4 mt-3">
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <AreasOfStrengths data={graphData?.data?.graphs?.strengths} />
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card h-100 border-0">
              <div className="card-body">
                <AreasOfDevelopment
                  data={graphData?.data?.graphs?.developments}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLoader && <BlockingSpinner />}
      {pdfProgress.show && <PdfProgressOverlay />}
    </>
  );
};

export default DashboardNewPage;
